import {
  Container,
  Heading,
  ListItem as ChakraListItem,
  Text as CharaText,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Link } from '~/common/components/link';
import type { Props as NoHeaderFooterLayoutProps } from '~/common/layouts/no-layout';
import { CopyrightProtectionBanner } from '~/features/handbook/community-guideline/components/copyright-protection-banner';
import { routes } from '~/utils/routes';
import { Color } from '~/utils/styles/enums';

const HandbookLayout = dynamic<any>(() =>
  import('~/common/layouts/handbook-layout').then(
    ({ HandbookLayout }) => HandbookLayout
  )
);

const NoLayout = dynamic<NoHeaderFooterLayoutProps>(() =>
  import('~/common/layouts/no-layout').then(
    ({ NoHeaderFooterLayout }) => NoHeaderFooterLayout
  )
);

export const Page: NextPage = () => {
  const { query } = useRouter();
  const noLayout = query['nolayout'] === '1';
  return (
    <>
      {noLayout ? (
        <NoLayout
          metaData={{
            title: routes['handbook']['community-guideline'].title(),
          }}
          pageInnerProps={{
            fullWidth: true,
          }}
          wrapperStyles={{
            background: Color.STRONG_BG,
          }}
        >
          <Content noLayout={noLayout} />
        </NoLayout>
      ) : (
        <HandbookLayout
          metaData={{
            title: routes['handbook']['community-guideline'].title(),
          }}
          breadcrumbList={[]}
          pageInnerProps={{
            fullWidth: true,
          }}
        >
          <Content noLayout={noLayout} />
        </HandbookLayout>
      )}
    </>
  );
};

const Content = ({ noLayout }: { noLayout: boolean }) => (
  <Container
    padding={{ base: '16px', md: noLayout ? '40px' : '0' }}
    background={Color.WHITE}
    borderRadius={
      noLayout
        ? undefined
        : {
            base: 0,
            md: '8px',
          }
    }
    maxWidth={noLayout ? '100%' : '3xl'}
  >
    <VStack spacing="8" alignItems="flex-start">
      <Heading as="h1" size="xl" color={Color.ACCENT_1000}>
        {routes['handbook']['community-guideline'].title()}
      </Heading>
      <Text>
        テラーノベルは誰でも自由に自分の創造性を発揮できる場所として、著作権等の権利が守られ、誰にとっても安全なコンテンツが掲載されているプラットフォームを目指しています。
        <br />
        <br />
        ユーザーがテラーノベルに掲載するすべての文章・画像において、以下の禁止事項を設けています。
        <br />
        <br />
        禁止事項に該当する文章・画像を掲載した場合、対象コンテンツの削除やサービスの利用制限・利用停止等の措置が行われる場合がございます。
        <br />
        <br />
        なお、本ガイドラインに掲載されている違反例は、あくまで一例であり、該当する禁止事項はこれらに限りません。本ガイドラインのほか、{' '}
        <AnchorLink
          href={`${routes.tos.url()}${noLayout ? '?nolayout=1' : ''}`}
        >
          {routes.tos.title()}
        </AnchorLink>
        にも禁止事項等の定めがありますので、利用規約・本ガイドラインをよく読み、ルールを守ってご利用いただくようお願いいたします。
      </Text>

      <Heading as="h1" size="lg" color={Color.ACCENT_1000}>
        禁止事項
      </Heading>

      {/* 著作権等の侵害 */}
      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="infringement">
        著作権等を侵害するもの
      </Heading>

      <VStack spacing="20px" alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>

        <UnorderedList spacing="2" pl="5">
          <ListItem>
            アニメ・マンガ・ドラマ・映画・ミュージックビデオ等の画像
          </ListItem>
          <ListItem>芸能人等の画像</ListItem>
          <ListItem>使用の許可を得ていない他人の写真・画像・イラスト</ListItem>
          <ListItem>
            アニメ・マンガ・ドラマ・映画・小説等の一定の長さのセリフ・文章
          </ListItem>
          <ListItem>歌詞</ListItem>
        </UnorderedList>
        <Text as="span">
          詳しくは以下の『画像アップロード時の注意
          〜著作権編〜』をご確認ください。
        </Text>

        <CopyrightProtectionBanner />
      </VStack>
      {/* 不適切な表現 */}
      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="inappropiate">
        不適切な出会いやわいせつ行為につながるもの
      </Heading>
      <Text as="span">
        テラーノベルは創造性を奨励するプラットフォームですが、安心安全の観点から不適切な内容の投稿は認めていません。以下の投稿・コメントを行うことを禁止しています。
      </Text>
      <VStack alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>
        <UnorderedList spacing="2" pl="5" marginY="4">
          <ListItem>
            電話番号・メールアドレス・LINE・カカオトーク・SNS等の連絡先
          </ListItem>
          <ListItem>QRコード（遷移先を問わず一律禁止）</ListItem>
          <ListItem>わいせつなやりとり</ListItem>
          <ListItem>作家活動と関係のない顔・身体の一部を含む写真</ListItem>
          <ListItem>
            不適切な出会いやわいせつ行為につながる内容・画像を予告・宣伝・要求する内容
          </ListItem>
        </UnorderedList>
        <Text>
          なお作家活動目的におけるSNS等のアカウントの表示や顔・身体の一部を含む写真の掲載等につきましては原則として禁止しておりませんが、個々の掲載内容、状況や背景を鑑みて、運営が不適切であると判断した場合には削除等の対応が行われる場合があります。
        </Text>
      </VStack>
      {/* 禁止行為 */}
      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="privacy">
        自己・他人のプライバシーにかかわるもの
      </Heading>
      <VStack alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>
        <UnorderedList spacing="2" marginY="4" pl="5">
          <ListItem>
            住所・電話番号・学校名等の個人の特定・接触が可能となる情報（他の情報と組み合わせることで、個人の特定・接触が可能となる情報を含む）
          </ListItem>
          <ListItem>
            売買・トレード（グッズ交換等）の実施を目的とする内容（売買・トレードの申し出を含む）
          </ListItem>
          <ListItem>掲載許可を得ていない他人の写真や実名等</ListItem>
          <ListItem>
            自己・第三者問わず個人情報（氏名、住所等を含みますが、これらに限りません。）を含む投稿
          </ListItem>
          <ListItem>人種・社会的身分・病歴等の配慮が必要な個人の情報</ListItem>
          <ListItem>
            自己・他人のプライバシーにかかわる内容・掲載画像を予告・宣伝・要求する内容
          </ListItem>
        </UnorderedList>
        <Text>
          作家活動を目的とした実名・顔写真の掲載等は原則として禁止しておりませんが、個々の掲載内容、状況や背景を鑑みて、運営が不適切と判断した場合には削除等の対応が行われる場合があります。
        </Text>
      </VStack>
      <Heading
        as="h2"
        size="lg"
        color={Color.ACCENT_1000}
        id="life-threatening"
      >
        生命の危険にかかわるもの
      </Heading>
      <VStack alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>
        <UnorderedList spacing="2" marginY="4" pl="5">
          <ListItem>自殺や自傷（リストカット等）を予告する内容</ListItem>
          <ListItem>自殺や自傷を推奨・助長する内容</ListItem>
          <ListItem>
            自殺や自傷に関連する質問・情報交換 <br />
            <br />
          </ListItem>
          <ListItem>
            その他、自身または他人の生命や心身に傷害を及ぼす可能性のある内容
          </ListItem>
        </UnorderedList>
      </VStack>

      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="honor">
        他人の尊厳・名誉を傷つけるもの
      </Heading>
      <VStack alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>
        <UnorderedList spacing="2" marginY="4" pl="5">
          <ListItem>誹謗中傷・いじめを目的とする内容</ListItem>
          <ListItem>誹謗中傷・いじめを推奨・助長する内容</ListItem>
          <ListItem>煽り・荒らしを目的とする内容</ListItem>
          <ListItem>人種等の個人の属性に基づく差別的な内容</ListItem>
          <ListItem>
            なりすまし <br />
            <br />
          </ListItem>
          <ListItem>他人の社会的評価を毀損しうる内容</ListItem>
        </UnorderedList>
      </VStack>

      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="criminal">
        犯罪行為
      </Heading>
      <VStack alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>
        <UnorderedList spacing="2" marginY="4" pl="5">
          <ListItem>犯罪行為を予告する内容</ListItem>
          <ListItem>犯罪行為を推奨・助長する内容</ListItem>
          <ListItem>犯罪行為を主張する内容</ListItem>
          <ListItem>犯罪行為に関する情報交換を目的とする内容</ListItem>
          <ListItem>その他、法律及び公序良俗に違反する内容</ListItem>
        </UnorderedList>
      </VStack>

      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="usage">
        円滑なサービス利用を妨げるもの
      </Heading>
      <VStack alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>
        <UnorderedList spacing="2" marginY="4" pl="5">
          <ListItem>スパム</ListItem>
          <ListItem>
            作家活動と無関係な広告・宣伝・勧誘を目的とする内容
          </ListItem>
          <ListItem>無意味な文字列の羅列</ListItem>
        </UnorderedList>
      </VStack>

      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="violent">
        性的・暴力的な内容・画像
      </Heading>
      <VStack alignItems="flex-start">
        <Text as="span">以下のようなコンテンツが該当します。</Text>
        <UnorderedList spacing="2" marginY="4" pl="5">
          <ListItem>卑猥な行為を直接的に描写した画像</ListItem>
          <ListItem>過度に露出した人物の画像</ListItem>
          <ListItem>過度に卑猥な衣服を着用した画像</ListItem>
          <ListItem>
            自殺・自傷・殺人または血液・傷を直接的に描写した画像
          </ListItem>
          <ListItem>
            その他、およそ創作表現として許容できない性的・暴力的な内容
          </ListItem>
        </UnorderedList>
        <Text>
          創作表現として許容できる範囲の画像や、文章における性的・暴力的表現については、センシティブフラグを設定するようお願いしております。
          <br />
          <br />
          センシティブフラグの設定目安は以下のヘルプページをご確認ください。
        </Text>
        <Text>
          ■ センシティブな内容の設定目安について <br />
          <AnchorLink
            href={`https://help.teller.jp/hc/ja/3-teller-novel/faq/140${
              noLayout ? '?nolayout=1' : ''
            }`}
          >
            https://help.teller.jp/hc/ja/3-teller-novel/faq/140
          </AnchorLink>
        </Text>
      </VStack>

      <Heading as="h2" size="lg" color={Color.ACCENT_1000} id="others">
        その他運営が不適切と判断したもの
      </Heading>
      <VStack alignItems="flex-start">
        <Text>
          個々の掲載内容、状況や背景を鑑みて対応が行われます。
          テラーノベルが行っている安心安全の取り組みについては、以下のページをご確認ください。
        </Text>
        <Text>
          ■ 安心安全への取り組み <br />
          <AnchorLink
            href={`${routes.safety.url()}${noLayout ? '?nolayout=1' : ''}`}
          >
            https://teller.jp{routes.safety.url()}
          </AnchorLink>
        </Text>
        <Text>
          また、コミュニティガイドラインに違反するコンテンツを見かけた場合は、報告機能を利用して運営に報告することができます。
          <br />
          <br />
          報告機能の使い方については、以下のページをご確認ください。
        </Text>
        <Text>
          ■作品・エピソード・コメント・ユーザーを報告したい <br />
          <AnchorLink
            href={`https://help.teller.jp/hc/ja/3-teller-novel/faq/180${
              noLayout ? '?nolayout=1' : ''
            }`}
          >
            https://help.teller.jp/hc/ja/3-teller-novel/faq/180
          </AnchorLink>
        </Text>
      </VStack>
    </VStack>
  </Container>
);

const Text = styled(CharaText)`
  color: ${Color.ACCENT_700};
  font-size: 1rem;
  word-break: break-all;
  font-family: 'Avenir Next', 'Hiragino Kaku Gothic Pro', 'ヒラギノ角ゴ Pro W3',
    'Hiragino Sans', Meiryo, メイリオ, Osaka, 'MS PGothic', arial, helvetica,
    sans-serif;
  line-height: 1.7;
`;

const ListItem = styled(ChakraListItem)`
  color: ${Color.ACCENT_700};
  font-size: 1rem;
  word-break: break-all;
  font-family: 'Avenir Next', 'Hiragino Kaku Gothic Pro', 'ヒラギノ角ゴ Pro W3',
    'Hiragino Sans', Meiryo, メイリオ, Osaka, 'MS PGothic', arial, helvetica,
    sans-serif;
  line-height: 1.7;
`;

const AnchorLink = styled(Link)`
  color: ${Color.TINT};
`;
